import { useEffect, useState } from 'react';
import { X } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { createFieldOrder } from '@/api/fieldOrders';
import { listParties, type Party } from '@/api/parties';
import { listItems, type Item } from '@/api/items';
import { CategoryItemPicker } from '@/components/form/CategoryItemPicker';
import { apiError } from '@/lib/apiError';
import { useFieldErrors } from '@/lib/useFieldErrors';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Row {
  itemId: number | null;
  itemName: string;
  qty: string;
  rate: string;
}

const empty: Row = { itemId: null, itemName: '', qty: '', rate: '' };

export function FieldOrderFormModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'salesperson' | 'customerName'>();
  const [salesperson, setSalesperson] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerType, setCustomerType] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [rows, setRows] = useState<Row[]>([{ ...empty }]);

  const partiesQ = useQuery({
    queryKey: ['parties', 'for-fo'],
    queryFn: () => listParties({ type: 'customer', pageSize: 10000 }),
    enabled: open,
  });
  const itemsQ = useQuery({
    queryKey: ['items', 'for-fo'],
    queryFn: () => listItems({ pageSize: 10000 }),
    enabled: open,
  });
  const customers = partiesQ.data?.data ?? [];
  const items = itemsQ.data?.data ?? [];

  useEffect(() => {
    if (open) {
      setSalesperson('');
      setCustomerId('');
      setCustomerName('');
      setCustomerType('');
      setDeliveryDate('');
      setRows([{ ...empty }]);
    }
  }, [open]);

  function pickCustomer(id: string) {
    setCustomerId(id);
    const p = customers.find((c: Party) => c.id === Number(id));
    if (p) {
      setCustomerName(p.name);
      setCustomerType(p.customerType ?? '');
    }
  }
  function pickItem(idx: number, itemId: string) {
    const item = items.find((i: Item) => i.id === Number(itemId));
    if (!item) return;
    setRows((cur) =>
      cur.map((r, i) =>
        i === idx
          ? { ...r, itemId: item.id, itemName: item.name, rate: String(item.mrp ?? '') }
          : r,
      ),
    );
  }

  const createMut = useMutation({
    mutationFn: createFieldOrder,
    onSuccess: (r) => {
      toast.pushStep(`Field Order ${r.orderNo} created`, {
        body: 'Waiting on approval before it enters the sales pipeline.',
        href: '/inventory/field-orders',
        nextLabel: 'Open Field Orders',
        tag: `fo-created-${r.id}`,
      });
      qc.invalidateQueries({ queryKey: ['field-orders'] });
      onClose();
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  function submit() {
    if (!fe.validate({
      salesperson: !salesperson.trim() && 'Salesperson is required',
      customerName: !customerName.trim() && 'Customer is required',
    })) return;
    const items = rows
      .filter((r) => r.itemName && Number(r.qty) > 0)
      .map((r) => ({
        itemId: r.itemId ?? undefined,
        itemName: r.itemName,
        qty: Number(r.qty),
        rate: r.rate ? Number(r.rate) : undefined,
      }));
    if (items.length === 0) {
      toast.push('Add at least one item', 'warning');
      return;
    }
    createMut.mutate({
      salesperson,
      customerId: customerId ? Number(customerId) : undefined,
      customerName,
      customerType: customerType || undefined,
      deliveryDate: deliveryDate || undefined,
      items,
    });
  }

  return (
    <Modal
      open={open}
      title="New Field Sales Order"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={createMut.isPending}>
            {createMut.isPending ? 'Creating…' : 'Create Order'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-2 sm:gap-3">
        <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 sm:gap-3">
          <FormGroup label="Salesperson *" error={fe.errors.salesperson}>
            <Input invalid={fe.has('salesperson')} value={salesperson} onChange={(e) => { setSalesperson(e.target.value); fe.clear('salesperson'); }} />
          </FormGroup>
          <FormGroup label="Customer (linked Party)">
            <Select value={customerId} onChange={(e) => pickCustomer(e.target.value)}>
              <option value="">— pick or type below —</option>
              {customers.map((c: Party) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.customerType ? `· ${c.customerType}` : ''}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="Customer Name (free-text fallback) *" error={fe.errors.customerName}>
            <Input
              invalid={fe.has('customerName')}
              value={customerName}
              onChange={(e) => { setCustomerName(e.target.value); fe.clear('customerName'); }}
            />
          </FormGroup>
          <FormGroup label="Customer Type">
            <Input
              value={customerType}
              onChange={(e) => setCustomerType(e.target.value)}
              placeholder="Wholesaler / Distributor / Retailer / MBO & SiS / Corporate & Projects / Architect / Customization"
            />
          </FormGroup>
          <FormGroup label="Delivery Date">
            <Input
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
            />
          </FormGroup>
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between text-[10px] uppercase text-[var(--text-muted)]">
            <span>Items</span>
            <Button
              variant="glass"
              size="sm"
              onClick={() => setRows((cur) => [...cur, { ...empty }])}
            >
              + Add Row
            </Button>
          </div>
          <div className="overflow-x-auto rounded-md border border-[var(--border-soft)]">
            <table className="w-full text-[11px]">
              <thead className="bg-[var(--bg-soft)] text-[10px] uppercase text-[var(--text-muted)]">
                <tr>
                  <th className="px-2 py-1 text-left">Item</th>
                  <th className="px-2 py-1 text-right">Qty</th>
                  <th className="px-2 py-1 text-right">Rate</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} className="border-t border-[var(--border-soft)]">
                    <td className="px-2 py-1">
                      <CategoryItemPicker
                        items={items.map((it: Item) => ({
                          id: it.id,
                          code: it.code,
                          name: it.name,
                          category: it.category,
                          currentStock: 0,
                        }))}
                        value={r.itemId ?? '__custom__'}
                        onCustom={() =>
                          setRows((cur) =>
                            cur.map((row, i) =>
                              i === idx ? { ...row, itemId: null } : row,
                            ),
                          )
                        }
                        onPick={(id) => pickItem(idx, String(id))}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        type="number"
                        step="1"
                        value={r.qty}
                        onChange={(e) =>
                          setRows((cur) =>
                            cur.map((row, i) => (i === idx ? { ...row, qty: e.target.value } : row)),
                          )
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={r.rate}
                        onChange={(e) =>
                          setRows((cur) =>
                            cur.map((row, i) =>
                              i === idx ? { ...row, rate: e.target.value } : row,
                            ),
                          )
                        }
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      {rows.length > 1 && (
                        <button
                          onClick={() => setRows((cur) => cur.filter((_, i) => i !== idx))}
                          className="text-rose-600 hover:underline"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
}
